import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Download, Play, Phone, Mail, ArrowRight, Package, Zap, Shield, Award, Clock, TrendingUp, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import SEOHead from './seo/SEOHead';
import Breadcrumbs from './seo/Breadcrumbs';
import InternalLinks from './seo/InternalLinks';
import ProductEnquiryForm from './product/ProductEnquiryForm';
import RelatedProducts from './product/RelatedProducts';
import GoogleReviewsStrip from './GoogleReviewsStrip';
import { JsonLd, productSchema, faqSchema } from './seo/schemas';

const capacityPageMap = {
  'wfm-30': { path: '/30-bpm-water-plant', label: '30 BPM Turnkey Water Plant' },
  'wfm-40': { path: '/40-bpm-water-plant', label: '40 BPM Turnkey Water Plant' },
  'wfm-60': { path: '/60-bpm-water-plant', label: '60 BPM Turnkey Water Plant' },
  'wfm-90': { path: '/90-bpm-water-plant', label: '90 BPM Turnkey Water Plant' },
  'wfm-120': { path: '/120-bpm-water-plant', label: '120 BPM Turnkey Water Plant' },
  'wfm-200': { path: '/200-bpm-water-plant', label: '200 BPM Turnkey Water Plant' }
};

const ProductPageTemplate = ({ productData }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const productPath = `/products/${productData.slug}`;
  const capacityPage = capacityPageMap[productData.id];

  const faqs = [
    {
      q: `What is the price of ${productData.capacity} capacity machine?`,
      a: `The ${productData.title} is priced in the range of ${productData.price?.range}. Final price depends on automation level, additional features, and customization requirements. Contact us for detailed quotation.`
    },
    {
      q: 'What is the delivery and installation timeline?',
      a: 'Standard delivery takes 30-45 days from order confirmation. Installation and commissioning typically takes 7-10 days depending on site readiness and complexity.'
    },
    {
      q: 'Do you provide training and after-sales support?',
      a: `Yes, we provide comprehensive operator training, ${productData.technicalDetails?.warranty} warranty, spare parts support, and AMC options for long-term peace of mind.`
    },
    {
      q: 'Can this machine be customized?',
      a: 'Absolutely! We specialize in custom solutions. Our engineering team can modify specifications, add features, or design completely custom machines based on your requirements.'
    }
  ];

  const handleInquiryClick = () => {
    document.getElementById('product-enquiry-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen pb-20 lg:pb-0">
      <SEOHead
        title={productData.metaTitle || `${productData.title} | Alden Technologies`}
        description={productData.metaDescription}
        path={productPath}
        image={productData.heroImage}
        type="product"
      />
      <JsonLd data={productSchema(productData, productPath)} />
      <JsonLd data={faqSchema(faqs)} />
      <Breadcrumbs items={[{ label: 'Products', to: '/products' }, { label: productData.title }]} />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-alden-green to-alden-green-light text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-white/20 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                {productData.category || 'Premium Equipment'}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                {productData.title}
              </h1>
              <p className="text-lg sm:text-xl mb-8 text-white/90">
                {productData.metaDescription}
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
                  <div className="text-sm text-white/80">Capacity</div>
                  <div className="text-xl font-bold">{productData.capacity}</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
                  <div className="text-sm text-white/80">Investment</div>
                  <div className="text-xl font-bold">{productData.price?.investment}</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={handleInquiryClick}
                  className="bg-white text-alden-green hover:bg-gray-100 py-6 px-8 text-lg font-semibold"
                >
                  Get Free Quotation
                  <ArrowRight className="ml-2" />
                </Button>
                <a href={`https://wa.me/919351292512?text=${encodeURIComponent(`Hi, I'm interested in ${productData.title}`)}`} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-alden-navy hover:bg-alden-navy-light text-white py-6 px-8 text-lg font-semibold">
                    <Phone className="mr-2" />
                    WhatsApp Now
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -top-4 -right-4 bg-yellow-400 text-alden-navy px-6 py-3 rounded-lg font-bold text-lg z-10 shadow-lg">
                {productData.price?.range}
              </div>
              <img 
                src={productData.heroImage} 
                alt={`${productData.title} - ${productData.capacity} mineral water bottling machine by Alden Technologies`}
                className="rounded-xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-4">
            <div className="md:col-span-3">
              <img 
                src={productData.gallery?.[selectedImage] || productData.heroImage} 
                alt={`${productData.title} installation view ${selectedImage + 1} - Alden Technologies`}
                className="w-full h-[400px] object-cover rounded-lg shadow-lg"
              />
            </div>
            <div className="flex md:flex-col gap-4">
              {productData.gallery?.map((img, idx) => (
                <img 
                  key={idx}
                  src={img} 
                  alt={`${productData.title} gallery thumbnail ${idx + 1}`}
                  loading="lazy"
                  onClick={() => setSelectedImage(idx)}
                  className={`w-full h-24 object-cover rounded-lg cursor-pointer transition-all ${
                    selectedImage === idx ? 'ring-4 ring-alden-green' : 'opacity-60 hover:opacity-100'
                  }`}
                />
              ))}
              {productData.videoUrl && (
                <a href={productData.videoUrl} target="_blank" rel="noopener noreferrer" className="w-full h-24 bg-alden-green rounded-lg flex items-center justify-center text-white hover:bg-alden-green-light transition-all">
                  <Play className="w-8 h-8" />
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <Card className="text-center border-2 border-alden-green/20 hover:border-alden-green transition-all">
              <CardContent className="p-6">
                <Package className="w-12 h-12 text-alden-green mx-auto mb-4" />
                <h3 className="font-bold text-lg mb-2">Premium Quality</h3>
                <p className="text-gray-600 text-sm">Food-grade SS 304/316 construction</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 border-alden-green/20 hover:border-alden-green transition-all">
              <CardContent className="p-6">
                <Zap className="w-12 h-12 text-alden-green mx-auto mb-4" />
                <h3 className="font-bold text-lg mb-2">High Efficiency</h3>
                <p className="text-gray-600 text-sm">Energy-efficient operations</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 border-alden-green/20 hover:border-alden-green transition-all">
              <CardContent className="p-6">
                <Shield className="w-12 h-12 text-alden-green mx-auto mb-4" />
                <h3 className="font-bold text-lg mb-2">Warranty</h3>
                <p className="text-gray-600 text-sm">{productData.technicalDetails?.warranty}</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 border-alden-green/20 hover:border-alden-green transition-all">
              <CardContent className="p-6">
                <Award className="w-12 h-12 text-alden-green mx-auto mb-4" />
                <h3 className="font-bold text-lg mb-2">BIS Compliant</h3>
                <p className="text-gray-600 text-sm">Meets all food safety standards</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Technical Specifications
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {Object.entries(productData.specifications || {}).map(([key, value]) => (
              <div key={key} className="flex items-start gap-4 bg-white p-6 rounded-lg shadow-md">
                <CheckCircle className="w-6 h-6 text-alden-green flex-shrink-0 mt-1" />
                <div>
                  <div className="font-semibold text-gray-700 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                  <div className="text-xl font-bold text-gray-900">{value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Key Features
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                {productData.idealFor}
              </p>
              <div className="space-y-4">
                {productData.features?.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-alden-green flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-alden-green to-alden-green-light p-8 rounded-xl text-white">
              <h3 className="text-2xl font-bold mb-6">Ideal For</h3>
              <div className="space-y-4">
                {productData.applications?.map((app, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm p-4 rounded-lg">
                    <TrendingUp className="w-6 h-6 flex-shrink-0" />
                    <span className="font-semibold">{app}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Details Table */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Technical Details
          </h2>
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
            <table className="w-full">
              <tbody>
                {Object.entries(productData.technicalDetails || {}).map(([key, value], idx) => (
                  <tr key={key} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-6 py-4 font-semibold text-gray-700 capitalize border-b">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </td>
                    <td className="px-6 py-4 text-gray-900 border-b">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Request Project Quotation - Enquiry Form */}
      <ProductEnquiryForm productTitle={productData.title} />

      {/* Google Reviews - Social Proof */}
      <GoogleReviewsStrip />

      {/* Capacity Landing Page Link */}
      {capacityPage && (
        <section className="py-10 bg-white">
          <div className="container mx-auto px-4">
            <Link
              to={capacityPage.path}
              className="block max-w-4xl mx-auto bg-alden-green/5 border-2 border-alden-green/30 hover:border-alden-green rounded-xl p-6 transition-all group"
              data-testid="capacity-page-link"
            >
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-sm font-semibold text-alden-green uppercase tracking-wider mb-1">Complete Turnkey Solution</div>
                  <div className="text-xl font-bold text-gray-900">
                    Planning a full plant? Explore the {capacityPage.label} — cost, layout, machinery &amp; ROI
                  </div>
                </div>
                <ArrowRight className="w-8 h-8 text-alden-green group-hover:translate-x-2 transition-transform" />
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {faqs.map((faq) => (
              <Card key={faq.q} className="border-2 border-gray-200 hover:border-alden-green transition-all">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg text-gray-900 mb-3 flex items-start gap-2">
                    <Info className="w-5 h-5 text-alden-green flex-shrink-0 mt-1" />
                    {faq.q}
                  </h3>
                  <p className="text-gray-700 pl-7">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-alden-green to-alden-green-light text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Get in touch with our experts for a free consultation and detailed quotation for {productData.title}.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-white text-alden-green hover:bg-gray-100 py-6 px-8 text-lg font-semibold">
                <Mail className="mr-2" />
                Request Quotation
              </Button>
            </Link>
            <a href={`https://wa.me/919351292512?text=${encodeURIComponent(`Hi, I need a quotation for ${productData.title}`)}`} target="_blank" rel="noopener noreferrer">
              <Button className="bg-alden-navy hover:bg-alden-navy-light text-white py-6 px-8 text-lg font-semibold">
                <Phone className="mr-2" />
                Call: +91 9351292512
              </Button>
            </a>
            {productData.brochureUrl && (
              <a href={productData.brochureUrl} download>
                <Button className="bg-white/10 hover:bg-white/20 text-white border-2 border-white py-6 px-8 text-lg font-semibold backdrop-blur-sm">
                  <Download className="mr-2" />
                  Download Brochure
                </Button>
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Related Products - Internal Linking */}
      <RelatedProducts currentId={productData.id} />

      <InternalLinks currentPath={productPath} />
    </div>
  );
};

export default ProductPageTemplate;
